import { HttpClient, HttpContext, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable, Subscriber } from 'rxjs';
import { map } from 'rxjs/operators';
import { MsalService } from '@azure/msal-angular';
import { CommonDataService } from '../common-data.service';
import { CONSTANTS } from '../../Constants/Constants';
import { SKIP_GLOBAL_ERROR } from '../../core/errors/http-context-tokens';
import { GlobalApiErrorHandlerService } from '../../core/errors/global-api-error-handler.service';
import { IApiProblemDetails } from '../../models/IApiProblemDetails';

export interface HttpOptions {
  headers?: HttpHeaders;
  params?: HttpParams;
  context?: HttpContext;
}

export interface SSEvent {
  event: string;
  data: string;
  problemDetails?: IApiProblemDetails;
}

export interface StreamEvent<T> {
  type: 'status' | 'result';
  status?: string;
  result?: T;
}

/**
 * Base HTTP service providing common HTTP methods for API communication.
 * Wraps Angular's HttpClient with simplified method signatures.
 * All methods return Observables that emit the typed response.
 */
@Injectable({
  providedIn: 'root'
})
export class BaseHttpService {

  private readonly msalService = inject(MsalService);
  private readonly commonDataService = inject(CommonDataService);
  private readonly globalApiErrorHandlerService = inject(GlobalApiErrorHandlerService);

  httpJsonHeaderOption = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json'
    }),
    withCredentials: false
  };

  constructor(private readonly http: HttpClient) { }

  /**
   * Performs an HTTP GET request to retrieve data from the server.
   * @param url - The endpoint URL to send the request to
   * @param params - Optional query parameters to append to the URL
   * @param options - Optional HTTP options (headers, additional params)
   * @returns Observable<T> - An observable that emits the typed response
   * @example
   * this.baseHttp.get<User[]>('/api/users');
   * this.baseHttp.get<Product>('/api/products', new HttpParams().set('id', '123'));
   */
  get<T>(url: string, params?: HttpParams, options?: HttpOptions): Observable<T> {
    return this.http.get<T>(url, {
      ...this.httpJsonHeaderOption,
      params,
      ...options
    });
  }

  /**
   * Performs an HTTP POST request to create a new resource on the server.
   * @param url - The endpoint URL to send the request to
   * @param body - The request body/payload to send
   * @param options - Optional HTTP options (headers, params)
   * @returns Observable<T> - An observable that emits the typed response
   * @example
   * this.baseHttp.post<User>('/api/users', { name: 'John', email: 'john@example.com' });
   */
  post<T>(url: string, body: unknown, options?: HttpOptions): Observable<T> {
    return this.http.post<T>(url, body, {
      ...this.httpJsonHeaderOption,
      ...options
    });
  }

  /**
   * Performs an HTTP PUT request to fully update an existing resource.
   * @param url - The endpoint URL to send the request to
   * @param body - The complete updated resource data
   * @param options - Optional HTTP options (headers, params)
   * @returns Observable<T> - An observable that emits the typed response
   * @example
   * this.baseHttp.put<User>('/api/users/1', { id: 1, name: 'John Updated', email: 'john@example.com' });
   */
  put<T>(url: string, body: unknown, options?: HttpOptions): Observable<T> {
    return this.http.put<T>(url, body, {
      ...this.httpJsonHeaderOption,
      ...options
    });
  }

  /**
   * Performs an HTTP PATCH request to partially update an existing resource.
   * @param url - The endpoint URL to send the request to
   * @param body - The partial data to update
   * @param options - Optional HTTP options (headers, params)
   * @returns Observable<T> - An observable that emits the typed response
   * @example
   * this.baseHttp.patch<User>('/api/users/1', { name: 'New Name' });
   */
  patch<T>(url: string, body: unknown, options?: HttpOptions): Observable<T> {
    return this.http.patch<T>(url, body, {
      ...this.httpJsonHeaderOption,
      ...options
    });
  }

  /**
   * Performs an HTTP DELETE request to remove a resource from the server.
   * @param url - The endpoint URL of the resource to delete
   * @param options - Optional HTTP options (headers, params)
   * @returns Observable<T> - An observable that emits the typed response
   * @example
   * this.baseHttp.delete<void>('/api/users/1');
   */
  delete<T>(url: string, body?: unknown, options?: HttpOptions): Observable<T> {
    return this.http.delete<T>(url, {
      ...this.httpJsonHeaderOption,
      body,
      ...options
    });
  }

  /**
   * Performs an HTTP HEAD request to retrieve headers without the response body.
   * Useful for checking if a resource exists or getting metadata.
   * @param url - The endpoint URL to send the request to
   * @param options - Optional HTTP options (headers, params)
   * @returns Observable<T> - An observable that emits the typed response
   * @example
   * this.baseHttp.head<void>('/api/files/123');
   */
  head<T>(url: string, options?: HttpOptions): Observable<T> {
    return this.http.head<T>(url, {
      ...this.httpJsonHeaderOption,
      ...options
    });
  }

  /**
   * Performs an HTTP OPTIONS request to retrieve supported HTTP methods for a URL.
   * Used for CORS preflight checks or API capability discovery.
   * @param url - The endpoint URL to query
   * @param options - Optional HTTP options (headers, params)
   * @returns Observable<T> - An observable that emits the typed response
   * @example
   * this.baseHttp.options<string[]>('/api/users');
   */
  options<T>(url: string, options?: HttpOptions): Observable<T> {
    return this.http.options<T>(url, {
      ...this.httpJsonHeaderOption,
      ...options
    });
  }

  /**
   * Performs an HTTP GET request to download binary data (files, images, etc.).
   * @param url - The endpoint URL of the file to download
   * @param params - Optional query parameters to append to the URL
   * @returns Observable<Blob> - An observable that emits the file as a Blob
   * @example
   * this.baseHttp.getBlob('/api/reports/download/123').subscribe(blob => {
   *   const url = URL.createObjectURL(blob);
   *   // Use url for download or display
   * });
   */
  getBlob(url: string, params?: HttpParams): Observable<Blob> {
    return this.http.get(url, {
      params,
      responseType: 'blob'
    });
  }

  /**
   * Performs a streaming HTTP POST request using Server-Sent Events (SSE).
   * Reads the response as a stream and emits each SSE event as it arrives.
   * @param url - The endpoint URL to send the request to
   * @param body - The request body/payload to send
   * @returns Observable<SSEvent> - An observable that emits each SSE event with its event type and data
   * @example
   * this.baseHttp.postStream('/api/operations/export', { id: '123' }).subscribe(event => {
   *   if (event.event === 'status') console.log(event.data);
   * });
   */
  postStream(url: string, body: unknown, options?: HttpOptions): Observable<SSEvent> {
    return new Observable<SSEvent>((subscriber: Subscriber<SSEvent>) => {
      this.executeStreamRequest(url, body, subscriber, options);
    });
  }

  postTypedStream<T>(url: string, body: unknown, options?: HttpOptions): Observable<StreamEvent<T>> {
    return this.postStream(url, body, options).pipe(
      map((sseEvent: SSEvent) => this.mapStreamEvent<T>(sseEvent))
    );
  }

  private async executeStreamRequest(url: string, body: unknown, subscriber: Subscriber<SSEvent>, options?: HttpOptions): Promise<void> {
    try {
      const token = this.msalService.instance.getActiveAccount()?.idToken;
      const estimateId = this.commonDataService.getEstimateId()?.trim() || CONSTANTS.DEFAULT_ESTIMATE_ID;
      const skipGlobalError = options?.context?.get(SKIP_GLOBAL_ERROR) ?? false;

      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'X-Estimate-Id': estimateId
      };
      
      if (options?.headers) {
        const customHeaders = options.headers.keys();
        for (const key of customHeaders) {
          headers[key] = options.headers.get(key) || '';
        }
      }
      
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify(body)
      });

      if (!response.ok) {
        const httpError = await this.createHttpErrorResponse(response, url);
          this.globalApiErrorHandlerService.handle(httpError, skipGlobalError);
        subscriber.error(httpError);
        return;
      }

      const reader = response.body?.getReader();
      if (!reader) {
        const httpError = new HttpErrorResponse({
          error: { detail: 'Empty response body' },
          status: response.status,
          statusText: response.statusText,
          url
        });
          this.globalApiErrorHandlerService.handle(httpError, skipGlobalError);
        subscriber.error(httpError);
        return;
      }

      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split('\n\n');
        buffer = parts.pop();

        for (const part of parts) {
          const sseEvent = this.parseSseEvent(part);
          if (!sseEvent) {
            continue;
          }

          if (sseEvent.event === 'error') {
            const httpError = this.createStreamHttpErrorResponse(response, url, sseEvent);
              this.globalApiErrorHandlerService.handle(httpError, skipGlobalError);
            subscriber.error(httpError);
            return;
          }

          subscriber.next(sseEvent);
        }
      }

      subscriber.complete();
    } catch (error) {
      subscriber.error(error);
    }
  }

  private mapStreamEvent<T>(sseEvent: SSEvent): StreamEvent<T> {
    if (sseEvent.event === 'status') {
      return { type: 'status', status: sseEvent.data };
    }

    return {
      type: 'result',
      result: JSON.parse(sseEvent.data) as T
    };
  }

  private parseSseEvent(part: string): SSEvent | null {
    const lines = part.trim().split('\n');
    let eventType = '';
    const dataLines: string[] = [];

    for (const line of lines) {
      if (line.startsWith('event: ')) {
        eventType = line.substring(7);
      } else if (line.startsWith('data: ')) {
        dataLines.push(line.substring(6));
      }
    }

    const rawData = dataLines.join('\n');
    if (!eventType && !rawData) {
      return null;
    }

    const problemDetails = eventType === 'error'
      ? this.tryParseProblemDetails(rawData)
      : undefined;

    return {
      event: eventType,
      data: problemDetails?.detail ?? rawData,
      problemDetails
    };
  }

  private tryParseProblemDetails(rawData: string): IApiProblemDetails | undefined {
    if (rawData) {
      try {
        const parsed = JSON.parse(rawData) as IApiProblemDetails;
        if (parsed && typeof parsed === 'object') {
          return parsed;
        }
      } catch {
        // Fall back to a minimal error shape for legacy SSE payloads.
      }
    }

    if (!rawData) {
      return undefined;
    }

    return {
      detail: rawData
    };
  }

  private createStreamHttpErrorResponse(response: Response, url: string, sseEvent: SSEvent): HttpErrorResponse {
    return new HttpErrorResponse({
      error: sseEvent.problemDetails ?? {
        detail: sseEvent.data
      },
      status: sseEvent.problemDetails?.status ?? response.status,
      statusText: sseEvent.problemDetails?.title ?? response.statusText,
      url
    });
  }

  private async createHttpErrorResponse(response: Response, url: string): Promise<HttpErrorResponse> {
    const responseText = await response.text();
    const errorPayload = this.tryParseResponseBody(responseText);

    return new HttpErrorResponse({
      error: errorPayload,
      status: response.status,
      statusText: response.statusText,
      url
    });
  }

  private tryParseResponseBody(responseText: string): unknown {
    if (!responseText) {
      return { detail: '' };
    }

    try {
      return JSON.parse(responseText);
    } catch {
      return { detail: responseText };
    }
  }

}
